import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Star, Globe, MapPin, Check, Quote, Navigation,
  Heart, ShieldCheck, Compass, User, Brain, Flower2, Sparkles, RefreshCw, Users,
  Hand, HeartHandshake, MessagesSquare,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, LineReveal } from "../components/Motion";
import { WhatsAppButton } from "../components/Chrome";
import { CtaBand } from "../components/Detail";
import { IMAGES, TOPIC_ORDER, METHOD_ORDER, ADDRESS, pad } from "../lib/site";

const Stars = ({ className = "h-3.5 w-3.5" }) => (
  <span className="flex gap-0.5" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`${className} ${i < 4 ? "fill-gold text-gold" : "fill-gold/40 text-gold/40"}`} />
    ))}
  </span>
);

const VALUE_ICONS = [Heart, ShieldCheck, Compass, User];

const Hero = () => {
  const { t } = useLanguage();
  const reduce = useReducedMotion();
  return (
    <section className="relative lg:overflow-hidden" data-testid="hero-section">
      <div className="relative h-[46vh] w-full lg:absolute lg:inset-0 lg:h-full">
        <img
          src={IMAGES.heroBg}
          alt="Naciye Rafiq-Arli vor der Hamburger HafenCity"
          className="absolute inset-0 h-full w-full object-cover object-[50%_18%] lg:hidden"
        />
        <img
          src={IMAGES.heroBgWide}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 hidden h-full w-full object-cover object-[50%_15%] lg:block"
        />
        <div className="absolute inset-0 bg-petrol/10" aria-hidden="true" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 pb-8 sm:px-8 lg:flex lg:min-h-[86vh] lg:items-center lg:px-12 lg:pb-0">
        <div className="-mt-20 w-full max-w-xl rounded-[2rem] border border-white/70 bg-white/90 p-6 shadow-2xl shadow-petrol/20 backdrop-blur-md sm:p-9 lg:mt-0 lg:bg-white/85">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark"
            data-testid="hero-overline"
          >
            {t.home.overline}
          </motion.p>
          <h1 className="mt-5" data-testid="hero-heading">
            <LineReveal
              lines={t.home.heroLines}
              className="hero-title font-display text-5xl font-semibold leading-[1.08] tracking-tight text-petrol sm:text-6xl"
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: reduce ? 0 : 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-slate2 sm:text-lg"
            data-testid="hero-subline"
          >
            {t.home.heroSub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <WhatsAppButton label={t.home.ctaPrimary} testId="hero-whatsapp-cta" className="px-6 py-3 text-sm" />
            <Link
              to="/ueber-mich"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border-2 border-petrol/15 px-6 py-2.5 text-sm font-bold text-petrol transition-colors hover:border-teal hover:text-teal-dark"
            >
              {t.home.about.cta}
            </Link>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
            data-testid="hero-values"
          >
            {t.home.values.map((value, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <li key={i} className="flex items-center gap-2 text-sm font-medium text-slate2">
                  <Icon className="h-4 w-4 text-teal-dark" aria-hidden="true" />
                  {value}
                </li>
              );
            })}
          </motion.ul>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.35 }}
            className="mt-7 flex items-center gap-3 border-t border-warmline pt-5"
          >
            <Stars />
            <span className="text-sm font-bold text-petrol" data-testid="google-rating-badge">{t.common.rating}</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SUPPORT_STYLES = [
  { icon: Brain, circle: "bg-petrol text-white" },
  { icon: Flower2, circle: "bg-teal text-white" },
  { icon: Sparkles, circle: "bg-sand text-petrol" },
  { icon: RefreshCw, circle: "bg-gold text-petrol" },
  { icon: Users, circle: "bg-teal-light text-teal-dark" },
];

const SupportGrid = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-white py-16 sm:py-20" data-testid="support-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div className="text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{t.home.support.overline}</p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-petrol sm:text-4xl">{t.home.support.title}</h2>
            <span className="mx-auto mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {t.about.helpList.items.map((item, i) => {
            const [title, ...rest] = item.split(":");
            const sub = rest.join(":").trim();
            const { icon: Icon, circle } = SUPPORT_STYLES[i % SUPPORT_STYLES.length];
            return (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  className="flex h-full flex-col items-center rounded-3xl border border-warmline bg-cream p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-teal"
                  data-testid={`support-card-${i}`}
                >
                  <span className={`flex h-14 w-14 items-center justify-center rounded-full ${circle}`}>
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-semibold leading-snug text-petrol">{title}</h3>
                  {sub && <p className="mt-2 text-xs leading-relaxed text-slate2 sm:text-sm">{sub}</p>}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const APPROACH_ICONS = { hypnose: Brain, eft: Hand, havening: HeartHandshake, gespraech: MessagesSquare };
const APPROACH_CIRCLES = ["bg-petrol text-white", "bg-gold text-petrol", "bg-teal text-white", "bg-sand text-petrol"];

const Approach = () => {
  const { t } = useLanguage();
  const a = t.home.approach;
  return (
    <section className="bg-cream py-16 sm:py-24" data-testid="approach-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:px-12">
        <Reveal>
          <img
            src={IMAGES.room}
            alt="Heller Therapieraum der Praxis in Hamburg-Alsterdorf"
            className="aspect-[4/3] w-full rounded-[2.5rem] object-cover shadow-lg shadow-petrol/10"
            loading="lazy"
          />
        </Reveal>
        <div>
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{a.overline}</p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-petrol sm:text-4xl">{a.title}</h2>
            <span className="mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
          </Reveal>
          <div className="mt-8 space-y-5">
            {a.rows.map((row, i) => {
              const Icon = APPROACH_ICONS[row.key] || Brain;
              return (
                <Reveal key={row.key} delay={i * 0.06}>
                  <div className="flex items-start gap-4" data-testid={`approach-row-${row.key}`}>
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${APPROACH_CIRCLES[i % APPROACH_CIRCLES.length]}`}>
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-semibold text-petrol sm:text-lg">{row.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate2">{row.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/arbeitsweise"
              data-testid="approach-cta"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-teal-dark hover:text-petrol"
            >
              {a.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const TopicsGrid = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="topics-bento-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div className="text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{t.home.topics.overline}</p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-petrol sm:text-4xl">{t.home.topics.title}</h2>
            <span className="mx-auto mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate2 sm:text-base">{t.home.topics.sub}</p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TOPIC_ORDER.map((slug, i) => {
            const item = t.topics.items[slug];
            const featured = slug === "trauma";
            return (
              <Reveal key={slug} delay={i * 0.04}>
                <Link
                  to={`/themen/${slug}`}
                  data-testid={`topic-card-${slug}`}
                  className={`group flex h-full flex-col justify-between rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
                    featured ? "border-petrol bg-petrol text-white" : "border-warmline bg-cream hover:border-teal"
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className={`font-mono text-xs font-semibold ${featured ? "text-teal-bright" : "text-gold-dark"}`} aria-hidden="true">
                        ({pad(i + 1)})
                      </span>
                      {item.badge && (
                        <span className="rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-petrol">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <h3 className={`mt-4 font-display text-lg font-semibold tracking-tight ${featured ? "text-white" : "text-petrol"}`}>
                      {item.title}
                    </h3>
                    <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-white/75" : "text-slate2"}`}>{item.teaser}</p>
                  </div>
                  <span className={`mt-5 inline-flex items-center gap-1.5 text-sm font-bold ${featured ? "text-teal-bright" : "text-teal-dark"}`}>
                    {t.common.readMore}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const MethodsBand = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-petrol py-16 sm:py-24" data-testid="methods-teaser-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-bright">{t.home.methods.overline}</p>
              <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">{t.home.methods.title}</h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-white/65 sm:text-base">{t.home.methods.sub}</p>
          </div>
        </Reveal>
        <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
          {METHOD_ORDER.map((slug, i) => (
            <Reveal key={slug} delay={i * 0.03}>
              <Link
                to={`/arbeitsweise/${slug}`}
                data-testid={`method-row-${slug}`}
                className="group flex items-center gap-5 py-5 transition-colors sm:gap-8 sm:px-4 sm:hover:bg-white/5"
              >
                <span className="font-mono text-sm font-semibold text-teal-bright/80" aria-hidden="true">
                  ({pad(i + 1)})
                </span>
                <span className="flex-1 font-display text-xl font-medium tracking-tight text-white transition-colors group-hover:text-teal-bright sm:text-2xl">
                  {t.methods.items[slug].title}
                </span>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-teal-bright opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8" delay={0.08}>
          <Link
            to="/arbeitsweise"
            data-testid="methods-teaser-cta"
            className="group inline-flex items-center gap-2 text-sm font-bold text-teal-bright hover:text-white"
          >
            {t.home.methods.cta}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const AboutTeaser = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-cream py-16 sm:py-24" data-testid="about-teaser-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:px-12">
        <div>
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{t.home.about.overline}</p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-petrol sm:text-4xl">{t.home.about.title}</h2>
            <span className="mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
            <p className="mt-5 leading-relaxed text-slate2 sm:text-lg">{t.home.about.text}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <ul className="mt-6 space-y-3">
              {t.home.about.facts.map((fact, i) => (
                <li key={i} className="flex items-center gap-3 text-ink">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-light">
                    <Check className="h-3.5 w-3.5 text-teal-dark" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-medium sm:text-base">{fact}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/ueber-mich"
              data-testid="about-teaser-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full border-2 border-petrol/15 px-6 py-2.5 text-sm font-bold text-petrol transition-colors hover:border-teal hover:text-teal-dark"
            >
              {t.home.about.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <Reveal className="relative">
          <div className="absolute -left-4 -top-4 h-24 w-24 rounded-full bg-teal-light" aria-hidden="true" />
          <img
            src={IMAGES.hafencity}
            alt="Naciye Rafiq-Arli vor der Hamburger HafenCity"
            className="relative aspect-[4/3] w-full rounded-[2.5rem] object-cover shadow-lg shadow-petrol/10"
            loading="lazy"
          />
          <div className="absolute -bottom-5 right-5 rounded-2xl border border-warmline bg-white px-4 py-3 shadow-lg">
            <Stars />
            <p className="mt-1 text-xs font-bold text-petrol">{t.common.rating}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-sand py-16 sm:py-24" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid items-start gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{t.home.testimonials.overline}</p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-petrol sm:text-4xl">{t.home.testimonials.title}</h2>
            <span className="mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
            <div className="mt-7 inline-flex flex-col rounded-3xl border border-warmline bg-white p-6">
              <p className="font-display text-4xl font-semibold text-petrol">4,8/5</p>
              <Stars className="h-4 w-4" />
              <p className="mt-2 text-sm text-slate2">{t.common.rating.split("·")[1]?.trim()}</p>
            </div>
            <div className="mt-6">
              <Link to="/feedback" data-testid="testimonials-more-link" className="group inline-flex items-center gap-2 text-sm font-bold text-teal-dark hover:text-petrol">
                {t.nav.feedback}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-4">
            {t.home.testimonials.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="rounded-3xl border border-warmline bg-white p-7 sm:p-8" data-testid={`testimonial-card-${i}`}>
                  <Quote className="h-6 w-6 text-gold" aria-hidden="true" />
                  <blockquote className="mt-3 font-display text-lg font-medium leading-relaxed text-petrol">
                    {item.quote}
                  </blockquote>
                  <figcaption className="mt-5 border-t border-warmline pt-4">
                    <Stars />
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Anfahrt = () => {
  const { t } = useLanguage();
  const a = t.home.anfahrt;
  return (
    <section id="anfahrt" className="bg-white py-16 sm:py-24" data-testid="anfahrt-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{a.overline}</p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-petrol sm:text-4xl">{a.title}</h2>
          <span className="mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
        </Reveal>
        <div className="mt-10 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal delay={0.05}>
            <div className="flex h-full flex-col justify-between rounded-3xl border border-warmline bg-cream p-7 sm:p-9">
              <div>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-teal text-white">
                  <MapPin className="h-5 w-5" aria-hidden="true" />
                </span>
                <p className="mt-5 font-display text-xl font-semibold text-petrol" data-testid="anfahrt-address">
                  {ADDRESS}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate2 sm:text-base">{a.text}</p>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Traute-Lafrenz-Stra%C3%9Fe+161%2C+22297+Hamburg"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="anfahrt-route-link"
                className="group mt-6 inline-flex w-fit items-center gap-2 rounded-full border-2 border-petrol/15 px-5 py-2.5 text-sm font-bold text-petrol transition-colors hover:border-teal hover:text-teal-dark"
              >
                <Navigation className="h-4 w-4" aria-hidden="true" />
                {a.routeCta}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-3xl border border-warmline shadow-sm" data-testid="anfahrt-map">
              <iframe
                title={a.mapTitle}
                src="https://maps.google.com/maps?q=Traute-Lafrenz-Stra%C3%9Fe%20161%2C%2022297%20Hamburg&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="h-64 w-full border-0 sm:h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main id="main-content">
      <Hero />
      <SupportGrid />
      <Approach />
      <TopicsGrid />
      <MethodsBand />
      <AboutTeaser />
      <Testimonials />
      <div className="bg-white px-5 py-16 sm:px-8 sm:py-20 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <CtaBand testId="home-cta-section" ctaTestId="home-cta-whatsapp" />
        </div>
      </div>
      <Anfahrt />
    </main>
  );
}
